/**
 * ErrorModal Component
 *
 * Reusable error dialog built on top of {@link Modal}.
 * Displays an error icon, title, message and a close button.
 *
 * Uses the UI toolkit translation provider for the default close label
 * (key: `modules.errorModal.close`). The label can also be supplied
 * directly via the `closeLabel` prop.
 *
 * @example
 * ```tsx
 * <ErrorModal
 *   open={hasError}
 *   title="Something went wrong"
 *   message="Unable to save changes. Please try again."
 *   onClose={() => setHasError(false)}
 * />
 * ```
 */
import * as React from 'react';
import { cn, getTestId, type TestIdProps } from '../../utils';
import { Modal } from '../../components/Modal';
import { Button } from '../../components/Button';
import { Icon } from '../../components/icons/Icon';
import { useTranslation } from '../../providers/UIProvider';

export interface ErrorModalProps extends TestIdProps {
  /** Whether the modal is visible */
  open: boolean;
  /** Error title */
  title: string;
  /** Error message — can be a string or rich ReactNode */
  message: React.ReactNode;
  /** Called when the modal is dismissed */
  onClose: () => void;
  /** Override label for the close button (defaults to translated `modules.errorModal.close`) */
  closeLabel?: string;
  /** Additional CSS classes for the modal wrapper */
  className?: string;
}

export const ErrorModal: React.FC<ErrorModalProps> = ({
  open,
  title,
  message,
  onClose,
  closeLabel,
  className,
  testId,
  id
}) => {
  const t = useTranslation('modules.errorModal');

  return (
    <Modal
      open={open}
      onClose={onClose}
      size='sm'
      className={cn('w-[90vw] sm:w-auto', className)}
      testId={getTestId({ testId, id })}
    >
      <Modal.Header
        onClose={onClose}
        label={title}
        description={message}
        icon={<Icon name='triangle-exclamation' className='text-error-text text-xl' />}
      />
      <Modal.Footer>
        <Modal.Action>
          <Button variant='primary' onClick={onClose}>
            {closeLabel ?? t('close')}
          </Button>
        </Modal.Action>
      </Modal.Footer>
    </Modal>
  );
};

ErrorModal.displayName = 'ErrorModal';
